if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Drag-aware full-image zoom (2026-07-16): pointerdown/up on an IMAGE
        // slide opens the same lightbox the corner icon opens, but only for a
        // genuine tap. Any movement >= 8px is a swipe and is left alone —
        // this preserves the 2026-07-06 fix (an image drag must never open
        // the zoom modal) exactly; only the stationary-tap case is new. Runs
        // regardless of whether this product has a thumbnails strip, so it
        // must stay ABOVE the thumbnails early-return below.
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('pointerdown', this.onMediaPointerDown.bind(this), { passive: true });
          this.elements.viewer.addEventListener('pointerup', this.onMediaPointerUp.bind(this), { passive: true });
          this.elements.viewer.addEventListener('pointercancel', this.onMediaPointerCancel.bind(this), { passive: true });
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onMediaPointerDown(event) {
        // Primary button/touch/pen only: right- and middle-click must not open
        // the lightbox (they'd fire alongside the native context menu).
        if (event.button !== 0) return;
        if (this.mediaTapStart) {
          // A second pointer went down while one was already tracked: this is
          // a pinch starting, not a tap. Abort tracking entirely.
          this.mediaTapStart = null;
          return;
        }
        const opener = event.target.closest('.product__modal-opener--image');
        if (!opener) return;
        this.mediaTapStart = {
          x: event.clientX,
          y: event.clientY,
          time: Date.now(),
          pointerId: event.pointerId,
          opener: opener,
        };
      }

      onMediaPointerUp(event) {
        const start = this.mediaTapStart;
        this.mediaTapStart = null;
        if (!start || event.pointerId !== start.pointerId) return;
        // The icon button already opens the modal via its own native click;
        // triggering our synthetic click too would open/close it twice.
        if (event.target.closest('.product__media-toggle')) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.sqrt(dx * dx + dy * dy) >= 8) return; // swipe: do nothing
        if (Date.now() - start.time >= 600) return; // long press: do nothing
        // Hover-zoom mode wires its own click handler onto the <img> (see
        // magnify.js); firing the lightbox too would open both at once.
        if (start.opener.querySelector('.image-magnify-hover')) return;
        const toggle = start.opener.querySelector('.product__media-toggle');
        if (toggle) toggle.click();
      }

      onMediaPointerCancel() {
        this.mediaTapStart = null;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
